import cn from 'classnames';
import map from 'lodash.map';
import filter from 'lodash.filter';
import React, { useContext, useState } from 'react';
import sortedArrow from '../../images/sorted_down_arrow.svg';
import questionCircle from '../../images/question_circle.svg';
import styles from './FundList.css';
import Expander from '../Expander/Expander';
import FundListItem from '../FundListItem/FundListItem';
import RiskRewardModal from '../RiskRewardModal/RiskRewardModal';
import QuickView from '../QuickView/QuickView';
import { CartContext } from '../../context/CartContext/CartContext';

import {
  isFundClosed,
  fundListConfig,
  fundYieldDates,
  sortFundList,
  getColumnWidth
} from './util';

const FirstHeaderRow = ({
  filteredColumns,
  reportFirstColIndex,
  reportType,
  setReportType,
  fundDates
}) => (
  <tr className="first-row">
    {/* Special header row above the column labels for average return/yield labeling */}
    {filteredColumns.map((column, i) => {
      // Do not render the special header for the 3 return/yield columns after the first one
      if (column.isAvgReport && i !== reportFirstColIndex) return false;

      // Render the special header spanning 4 cols from the first column
      if (i === reportFirstColIndex) {
        return (
          <th
            colSpan="4"
            key={column.i}
            scope="colgroup"
            className="table-header top returns">
            <div className="report-types">
              <div
                onClick={() => setReportType('average')}
                onKeyDown={() => setReportType('average')}
                className={cn(
                  { inactive: reportType !== 'average' },
                  'average-returns-btn'
                )}
                role="button"
                tabIndex="0">
                Average Returns
              </div>
              <div
                onClick={() => setReportType('yield')}
                onKeyDown={() => setReportType('yield')}
                className={cn(
                  { inactive: reportType === 'average' },
                  'seven-day-btn'
                )}
                role="button"
                data-name="sevenDayYield"
                tabIndex="0">
                7 Day SEC Yield
              </div>
            </div>
            <div className="report-th-label">
              {reportType === 'average' ? (
                <>Quarterly as of {fundDates.averageAnnualQuarterlyDate}</>
              ) : (
                <>
                  {' '}
                  <div>
                    Simple
                    <br />
                    as of {fundDates.simpleDate}
                  </div>
                </>
              )}
            </div>
          </th>
        );
      }
      // eslint-disable-next-line
      return <th colSpan="1" key={i} className="table-header top"></th>;
    })}
    <style jsx>{styles}</style>
  </tr>
);

const SortArrow = ({ column, sortProperty, sortDirection }) =>
  column.sortProperty === sortProperty && (
    <div className="sort-header-container">
      <img
        className={`sort-arrow arrow-${sortDirection ? 'down' : 'up'}`}
        src={sortedArrow}
        alt={`${sortDirection ? 'Sorted descending' : 'Sorted ascending'}`}
      />
      <style jsx>{styles}</style>
    </div>
  );

const SecondHeaderRow = ({
  filteredColumns,
  setSortProperty,
  setSortDirection,
  sortDirection,
  sortProperty,
  toggleRiskRewardModal
}) => {
  const sortColumn = column => () => {
    setSortDirection(sortProperty === column.sortProperty && !sortDirection);
    setSortProperty(column.sortProperty);
  };

  return (
    <tr className="second-row">
      {map(filteredColumns, (column, i) => (
        <th
          key={i}
          scope="col"
          id={column?.id}
          style={{ minWidth: getColumnWidth(column) }}
          className={`table-header ${column.isAvgReport &&
            'returns'} ${column.isNumber && 'number'}`}>
          <div
            role="button"
            className="sort-header-container"
            tabIndex="0"
            onClick={sortColumn(column)}
            onKeyDown={sortColumn(column)}>
            {column.displayName}

            {column.sortProperty === 'riskLevel' && (
              <button
                type="button"
                className="risk-tool-tip"
                onClick={() => toggleRiskRewardModal(240)}>
                <img
                  src={questionCircle}
                  alt="Click to view more information"
                />
              </button>
            )}
          </div>
          <SortArrow
            column={column}
            sortProperty={sortProperty}
            sortDirection={sortDirection}
          />
        </th>
      ))}
      <style jsx>{styles}</style>
    </tr>
  );
};

const FundList = ({
  groupedFunds,
  groupedFundsTotals,
  filtersApplied,
  chosen
}) => {
  const {
    api: { addFund, fundAdded, removeFund }
  } = useContext(CartContext);

  const [sortProperty, setSortProperty] = useState('name');
  const [sortDirection, setSortDirection] = useState(false);
  const [reportType, setReportType] = useState('average');
  const [quickViews, setQuickViews] = useState({});
  const [expanders, setExpander] = useState(
    chosen === 'viewAllMoneyMarketFunds'
      ? { 'Tax-Free Money Market': true, 'Taxable Money Market': true }
      : {}
  );

  const [riskRewardModal, setRiskRewardModal] = useState({
    riskLevel: 240,
    open: false,
    fund: {}
  });

  const toggleRiskRewardModal = (riskLevel = 240, fund = {}) => {
    setRiskRewardModal({ riskLevel, fund });
    const modal = document.querySelector('#riskRewardModal');
    if (modal !== null) {
      modal.show();
    }
  };

  const toggleQuickView = ticker => {
    const currentQuickViews = quickViews;
    const newQuickViews = {};
    newQuickViews[ticker] = !currentQuickViews[ticker];
    setQuickViews(newQuickViews);
  };

  const fundDates = fundYieldDates(groupedFunds);
  const filteredColumns = filter(fundListConfig.columns, column => {
    return !column.isAvgReport || column.reportType === reportType;
  });
  const reportFirstColIndex = filteredColumns.findIndex(
    column => column.isAvgReport && column.reportType === reportType
  );

  const sortedFunds = sortFundList(groupedFunds, sortProperty, sortDirection);

  return (
    <div className="fund-list-wrapper">
      <table className="fund-list">
        <caption>
          Funds list table which displays fund details, fund ratings and returns
          for each fund
        </caption>
        <thead>
          <FirstHeaderRow
            filteredColumns={filteredColumns}
            reportFirstColIndex={reportFirstColIndex}
            reportType={reportType}
            setReportType={setReportType}
            fundDates={fundDates}
          />
          <SecondHeaderRow
            filteredColumns={filteredColumns}
            setSortProperty={setSortProperty}
            setSortDirection={setSortDirection}
            sortDirection={sortDirection}
            sortProperty={sortProperty}
            toggleRiskRewardModal={toggleRiskRewardModal}
          />
        </thead>
        {map(sortedFunds, (grouping, name) => {
          const headerTemplate = filtersApplied
            ? `${name} (${grouping.length} of ${groupedFundsTotals[name]})`
            : `${name} (${groupedFundsTotals[name]})`;

          return (
            <Expander
              setExpander={setExpander}
              expanders={expanders}
              headerTemplate={headerTemplate}
              id={name}
              isOpen={expanders[name]}
              autoAdjust
              key={grouping.name}>
              {grouping.map((fund, j) => {
                const fundItem = [
                  <FundListItem
                    disabled={isFundClosed(fund)}
                    fund={fund}
                    id={j}
                    key={grouping.j}
                    toggleRiskRewardModal={toggleRiskRewardModal}
                    isQuickViewOpen={quickViews[fund.ticker]}
                    isSelected={false}
                    onTitleClick={toggleQuickView}
                    reportType={reportType}
                    addFund={addFund}
                    removeFund={removeFund}
                    fundAdded={fundAdded}
                  />
                ];

                if (quickViews[fund.ticker]) {
                  fundItem.push(
                    <tr>
                      <td colSpan="100">
                        <QuickView
                          fund={fund}
                          id={j}
                          disabled={isFundClosed(fund)}
                          addFund={addFund}
                          removeFund={removeFund}
                          fundAdded={fundAdded}
                          onClose={toggleQuickView}
                        />
                      </td>
                    </tr>
                  );
                }

                return fundItem;
              })}

              <tr>
                <td className="morningstar-legal" colSpan="10">
                  Morningstar Ratings&trade; are based on risk-adjusted returns.
                  <br />
                  Click on &quot;Overall Morningstar Rating&trade;&quot; for the
                  fund&apos;s 3-, 5-, and 10-year (if applicable) Morningstar
                  Ratings&trade;.
                </td>
              </tr>
            </Expander>
          );
        })}
      </table>
      <RiskRewardModal
        riskLevel={riskRewardModal.riskLevel}
        fund={riskRewardModal.fund}
      />
      <style jsx>{styles}</style>
    </div>
  );
};

FundList.defaultProps = {
  funds: []
};

export default FundList;
